// Keep production authentication/API requests pointed at the API even if a
// Pages environment was built without the optional public override.
export const API_URL = import.meta.env.VITE_API_URL || "https://api.workviewpro.com";
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || "dev";
export const ENABLE_LOGS = import.meta.env.VITE_ENABLE_LOGS === "true";
export const IS_PREVIEW_ENVIRONMENT = import.meta.env.VITE_PREVIEW_ENVIRONMENT === "true";

export async function apiFetch(
  path: string,
  options: RequestInit = {},
  settings: { log?: boolean } = {},
) {
  const url = `${API_URL}${path}`;
  const shouldLog = ENABLE_LOGS && settings.log !== false;

  if (shouldLog) {
    console.info(`[workview] API request: ${options.method ?? "GET"} ${url}`);
  }

  const response = await fetch(url, {
    ...options,
    credentials: "include",
  });

  if (shouldLog) {
    console.info(`[workview] API response: ${response.status} ${url}`, {
      requestId: response.headers.get("X-Request-ID") ?? "not provided",
    });
  }

  return response;
}

/**
 * A read request must not leave an operational surface spinning forever when a
 * network connection or upstream Worker stalls. The caller's signal still
 * wins (for unmount/navigation); the private controller supplies the timeout.
 */
export async function apiFetchWithTimeout(
  path: string,
  options: RequestInit = {},
  timeoutMs = 8_000,
) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  const timer = window.setTimeout(abort, timeoutMs);
  try {
    return await apiFetch(path, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener("abort", abort);
  }
}

export async function healthCheck() {
  const response = await apiFetch("/api/v1/health");

  if (!response.ok) {
    throw new Error(`Health check failed with status ${response.status}`);
  }

  return response.json();
}
